import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // The backend intentionally returns the same 200 whether or not the
      // email is registered, so we don't need to interpret the response —
      // just show the confirmation screen once the request completes.
      await axios.post(`${API}/auth/request-password-reset`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 mb-8" data-testid="forgot-brand">
          <TrendingUp className="w-5 h-5 text-blue-500" />
          <span className="font-heading font-semibold text-lg tracking-tight">ZigZagX</span>
        </Link>

        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-6" data-testid="forgot-password-card">
          {sent ? (
            <div className="space-y-3" data-testid="forgot-sent">
              <h1 className="font-heading text-xl uppercase tracking-wider text-zinc-100">Check your inbox</h1>
              <p className="text-sm text-zinc-400 leading-relaxed">
                If <span className="text-zinc-200 font-mono">{email}</span> is registered, a password-reset link is on
                its way. The link expires in 30 minutes.
              </p>
              <p className="text-xs text-zinc-600 leading-relaxed">
                Didn't see it? Check your spam folder, or try again in a minute.
              </p>
              <div className="pt-2">
                <Link to="/login">
                  <Button variant="ghost" className="rounded-sm text-zinc-400 hover:text-zinc-100 h-8 text-xs uppercase tracking-wider" data-testid="forgot-back-to-login">
                    ← Back to sign in
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h1 className="font-heading text-xl uppercase tracking-wider text-zinc-100 mb-1">Forgot your password?</h1>
                <p className="text-sm text-zinc-500 leading-relaxed">
                  Enter the email address on your account. We'll send you a link to reset your password.
                </p>
              </div>
              <div>
                <Label htmlFor="fp-email" className="text-xs uppercase tracking-wider text-zinc-500">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono"
                />
              </div>
              <Button type="submit" disabled={busy || !email.trim()} data-testid="forgot-submit-btn" className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 h-10 disabled:opacity-50">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
              <div className="text-center pt-1">
                <Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-300 uppercase tracking-wider" data-testid="forgot-back-link">
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
